import React, { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { useFeaturedVehicles } from "../lib/useFeaturedVehicles";
import { carouselSection } from "../content/landingContent";
import { useContentField, useLinks } from "../lib/useContent";
import { trackViewContent } from "../lib/metaPixel";
import { Reveal } from "./Reveal";
import { CtaButton } from "./CtaButton";
import { VehicleCard } from "./VehicleCard";

export const VehicleCarousel: React.FC = () => {
  const vehicles = useFeaturedVehicles();
  const links = useLinks();
  const title = useContentField("carouselSection.title", carouselSection.title);
  const subtitle = useContentField("carouselSection.subtitle", carouselSection.subtitle);
  const exploreAll = useContentField("carouselSection.exploreAll", carouselSection.exploreAll);
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: "start", slidesToScroll: 1 },
    [Autoplay({ delay: 4500, stopOnInteraction: true, stopOnMouseEnter: true })],
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  return (
    <section
      id="vehiculos"
      data-testid="vehicle-carousel-section"
      className="bg-white py-20 sm:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <Reveal className="max-w-xl">
            <span className="font-display text-xs font-semibold uppercase tracking-[0.2em] text-motor-red">
              Capítulo 02
            </span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl lg:text-[2.75rem] text-motor-ink leading-tight">
              {title}
            </h2>
            <p className="mt-4 text-base text-neutral-600 leading-relaxed">
              {subtitle}
            </p>
          </Reveal>

          <div className="hidden lg:flex gap-3">
            <button
              data-testid="carousel-prev-button"
              onClick={() => emblaApi?.scrollPrev()}
              aria-label="Vehículo anterior"
              className="h-12 w-12 rounded-full border-2 border-motor-ink flex items-center justify-center hover:bg-motor-ink hover:text-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="carousel-next-button"
              onClick={() => emblaApi?.scrollNext()}
              aria-label="Siguiente vehículo"
              className="h-12 w-12 rounded-full border-2 border-motor-ink flex items-center justify-center hover:bg-motor-ink hover:text-white transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="mt-10 overflow-hidden" ref={emblaRef} data-testid="vehicle-carousel-viewport">
          <div className="flex -ml-4">
            {vehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                className="pl-4 shrink-0 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
              >
                <VehicleCard vehicle={vehicle} />
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-2 lg:hidden">
          {scrollSnaps.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Ir al vehículo ${idx + 1}`}
              data-testid={`carousel-dot-${idx}`}
              onClick={() => emblaApi?.scrollTo(idx)}
              className={`h-1.5 rounded-full transition-all ${
                idx === selectedIndex ? "w-6 bg-motor-red" : "w-1.5 bg-neutral-300"
              }`}
            />
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <CtaButton
            href={links.exploreUrl}
            onClick={() => trackViewContent("vehicle_carousel_explore_all")}
            variant="outline"
            testId="carousel-explore-all-button"
            icon={<ArrowRight className="h-4 w-4" />}
          >
            {exploreAll}
          </CtaButton>
        </div>
      </div>
    </section>
  );
};
